import React, { useEffect, useState } from "react";
import {
  FaFilter,
  FaTimes,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUserCheck,
  FaCalendarAlt,
  FaMobile,
  FaEnvelope,
  FaTag,
  FaEye,
  FaCalendarDay,
} from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import { getDepositHistory } from "../../api/endpoints";
import PageHeader from "../../components/PageHeader";

const DepositHistoryList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilter, setShowFilter] = useState(false);

  // ✅ API se baseUrl aayega (e.g. "http://192.168.1.27:5000")
  const [imagePath, setImagePath] = useState("");

  // ============================================================
  // FETCH DEPOSIT HISTORY
  // ============================================================
  const fetchDepositHistory = async () => {
    try {
      setLoading(true);

      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (paymentMethodFilter) params.paymentMethod = paymentMethodFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await getDepositHistory(params);
      console.log("Deposit Response:", res);

      // ✅ Response structure handle karo
      const payload = res?.data?.data || res?.data || res;

      setTransactions(payload?.transactions || []);
      setTotalPages(payload?.totalPages || 1);
      setTotalItems(payload?.total || 0);
      if (payload?.page) setPage(payload.page);

      // ✅ API me "baseUrl" field aa rahi hai — usse use karo
      const baseUrl =
        payload?.baseUrl ||
        res?.data?.baseUrl ||
        res?.baseUrl ||
        process.env.REACT_APP_IMAGE_PATH ||
        "";

      if (baseUrl) {
        setImagePath(baseUrl);
        console.log("✅ Image Base URL set:", baseUrl);
      } else {
        console.warn("⚠️ baseUrl nahi mila API response me");
      }
    } catch (error) {
      console.error("Error fetching deposit history:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to fetch deposit history",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================
  useEffect(() => {
    fetchDepositHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, paymentMethodFilter, startDate, endDate, page]);

  // ============================================================
  // IMAGE URL BUILDER
  // ============================================================
  const getImageUrl = (img) => {
    if (!img) return "";

    // Already full URL
    if (img.startsWith("http://") || img.startsWith("https://")) {
      return img;
    }

    // Base64
    if (img.startsWith("data:")) {
      return img;
    }

    // Join with imagePath (baseUrl from API)
    const basePath = (imagePath || "").replace(/\/+$/, "");
    const fileName = img.replace(/^\/+/, "");

    if (!basePath) {
      console.warn("⚠️ imagePath empty — returning relative path:", img);
      return img;
    }

    return `${basePath}/${fileName}`;
  };

  // ============================================================
  // VIEW SCREENSHOT (SweetAlert)
  // ============================================================
  const viewScreenshot = (screenshot) => {
    const url = getImageUrl(screenshot);
    if (!url) {
      Swal.fire("Info", "No screenshot available", "info");
      return;
    }
    console.log("Opening screenshot URL:", url);

    Swal.fire({
      title: "Transaction Screenshot",
      imageUrl: url,
      imageWidth: 400,
      imageHeight: "auto",
      imageAlt: "Screenshot",
      confirmButtonText: "Close",
      showCloseButton: true,
    });
  };

  // ============================================================
  // HELPERS
  // ============================================================
  const getStatusBadge = (status) => {
    const statusMap = {
      APPROVED: { color: "bg-success", icon: <FaCheckCircle />, text: "Approved" },
      PENDING: { color: "bg-warning text-dark", icon: <FaClock />, text: "Pending" },
      REJECTED: { color: "bg-danger", icon: <FaTimesCircle />, text: "Rejected" },
    };
    const s = statusMap[status] || statusMap.PENDING;
    return (
      <span className={`badge ${s.color} d-inline-flex align-items-center gap-1`}>
        {s.icon} {s.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const goToPage = (n) => {
    if (n >= 1 && n <= totalPages) setPage(n);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPaymentMethodFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const hasActiveFilters =
    search || statusFilter || paymentMethodFilter || startDate || endDate;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lead-page">
      <PageHeader title="Deposit History" subtitle="View all deposit transactions" />

      <div className="card mb-4 card-light">
        {/* ===== Card Header with Filters ===== */}
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">Deposit Transactions</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <div className="input-group" style={{ width: "200px" }}>
                  <span className="input-group-text">
                    <FaSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search user/mobile..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                </select>

                <select
                  className="form-select"
                  value={paymentMethodFilter}
                  onChange={(e) => {
                    setPaymentMethodFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: "150px" }}
                >
                  <option value="">All Methods</option>
                  <option value="UPI">UPI</option>
                  <option value="ADMIN">Admin</option>
                  <option value="BANK">Bank</option>
                </select>

                <div className="input-group" style={{ width: "170px" }}>
                  <span className="input-group-text">
                    <FaCalendarAlt />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <div className="input-group" style={{ width: "170px" }}>
                  <span className="input-group-text">
                    <FaCalendarDay />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                {hasActiveFilters && (
                  <button className="btn btn-outline-danger" onClick={clearFilters}>
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter /> {showFilter ? "Hide" : "Filter"}
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={fetchDepositHistory}
              disabled={loading}
            >
              {loading ? <span className="spinner-border spinner-border-sm" /> : "Refresh"}
            </button>
          </div>
        </div>

        {/* ===== Card Body ===== */}
        <div className="card-body">
          {/* Stats Bar */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {transactions.length} of {totalItems} transactions
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active Filters Chips */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setStatusFilter("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {paymentMethodFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Method: {paymentMethodFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setPaymentMethodFilter("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {startDate && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  From: {formatDateDisplay(startDate)}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setStartDate("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {endDate && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  To: {formatDateDisplay(endDate)}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setEndDate("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* ===== TABLE ===== */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User Details</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>UTR Number</th>
                  <th>Status</th>
                  <th>Screenshot</th>
                  <th>Approved By</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && transactions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length > 0 ? (
                  transactions.map((tx, index) => {
                    const displayName =
                      tx.userName?.trim() ||
                      (tx.userMobile ? `User (${tx.userMobile})` : "Unknown User");

                    const avatarChar =
                      tx.userName?.charAt(0) ||
                      tx.userMobile?.charAt(0) ||
                      "U";

                    const imgUrl = getImageUrl(tx.screenshot);

                    return (
                      <tr key={tx._id}>
                        <td>{(page - 1) * limit + index + 1}</td>

                        {/* User Details */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                              style={{
                                width: 35,
                                height: 35,
                                fontSize: 14,
                                fontWeight: "bold",
                                flexShrink: 0,
                              }}
                            >
                              {avatarChar.toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-bold">{displayName}</div>
                              {tx.userMobile && (
                                <small className="text-muted d-block">
                                  <FaMobile className="me-1" size={11} />
                                  {tx.userMobile}
                                </small>
                              )}
                              {tx.userEmail && (
                                <small className="text-muted d-block">
                                  <FaEnvelope className="me-1" size={11} />
                                  {tx.userEmail}
                                </small>
                              )}
                              {tx.referralCode && (
                                <small className="text-muted d-block">
                                  <FaTag className="me-1" size={11} />
                                  Ref: {tx.referralCode}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Amount */}
                        <td>
                          <span className="fw-bold text-success">
                            {formatCurrency(tx.amount)}
                          </span>
                        </td>

                        {/* Payment Method */}
                        <td>
                          <span
                            className={`badge ${
                              tx.paymentMethod === "ADMIN"
                                ? "bg-secondary"
                                : "bg-info text-dark"
                            }`}
                          >
                            {tx.paymentMethod || "N/A"}
                          </span>
                        </td>

                        {/* UTR */}
                        <td>
                          <code className="bg-light p-1 rounded small">
                            {tx.utrNumber || "N/A"}
                          </code>
                        </td>

                        {/* Status */}
                        <td>{getStatusBadge(tx.status)}</td>

                        {/* Screenshot */}
                        <td>
                          {tx.screenshot ? (
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={imgUrl}
                                alt="screenshot"
                                style={{
                                  width: 45,
                                  height: 45,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  border: "1px solid #E2E8F0",
                                  background: "#F8FAFC",
                                }}
                                onClick={() => viewScreenshot(tx.screenshot)}
                                onError={(e) => {
                                  console.error(
                                    "Screenshot failed to load:",
                                    e.currentTarget.src
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => viewScreenshot(tx.screenshot)}
                              >
                                <FaEye /> View
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted small">No image</span>
                          )}
                        </td>

                        {/* Approved By */}
                        <td>
                          {tx.approvedBy ? (
                            <span className="text-muted small">
                              <FaUserCheck className="me-1" />
                             Admin
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        {/* Date */}
                        <td>
                          <div className="d-flex flex-column small">
                            <span>{formatDate(tx.createdAt)}</span>
                            {tx.approvedAt && (
                              <small className="text-muted">
                                ✓ {formatDate(tx.approvedAt)}
                              </small>
                            )}
                            {tx.remark && (
                              <small className="text-danger">
                                <FaTag className="me-1" size={10} />
                                {tx.remark}
                              </small>
                            )}
                            <small className="text-muted">
                              Order: {tx.orderId}
                            </small>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      {hasActiveFilters
                        ? "No transactions match your filters"
                        : "No Deposit Transactions Found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ===== Pagination ===== */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>
              <div className="d-flex gap-1">
                {getPageNumbers().map((n) => (
                  <button
                    key={n}
                    className={`btn ${
                      n === page ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => goToPage(n)}
                    disabled={loading}
                    style={{ minWidth: 40 }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          {/* Footer Info */}
          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {transactions.length} of {totalItems} transactions | Page{" "}
              {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositHistoryList;